// src/pages/Orders/OrderDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { orderService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
    PencilIcon,
    ArrowLeftIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

const OrderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasRole, hasAnyRole } = useAuth();
    const [order, setOrder] = useState(null);
    const [fulfillability, setFulfillability] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fulfillabilityLoading, setFulfillabilityLoading] = useState(false);
    const [processingOrder, setProcessingOrder] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                setLoading(true);
                const response = await orderService.getOrder(id);
                setOrder(response.data);
                setError(null);

                // Check fulfillability if the order is pending
                if (response.data.status === 'pending') {
                    checkFulfillability();
                }
            } catch (err) {
                console.error('Error fetching order details:', err);
                setError('Failed to load order details. Please try again later.');
                toast.error('Failed to load order details');
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [id]);

    // Check if order can be fulfilled based on inventory
    const checkFulfillability = async () => {
        try {
            setFulfillabilityLoading(true);
            const response = await orderService.checkFulfillability(id);
            setFulfillability(response.data);
        } catch (err) {
            console.error('Error checking fulfillability:', err);
            toast.error('Failed to check order fulfillability');
        } finally {
            setFulfillabilityLoading(false);
        }
    };

    // Process the order
    const processOrder = async () => {
        try {
            setProcessingOrder(true);
            await orderService.processOrder(id);
            toast.success('Order processed successfully');

            // Refresh order data
            const response = await orderService.getOrder(id);
            setOrder(response.data);
        } catch (err) {
            console.error('Error processing order:', err);
            toast.error(err.response?.data?.message || 'Failed to process order');
        } finally {
            setProcessingOrder(false);
        }
    };

    // Get status badge color
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'processing':
                return 'bg-blue-100 text-blue-800';
            case 'fulfilled':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                    <Link to="/orders" className="text-gray-500 hover:text-gray-700">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </Link>
                    <h1 className="text-2xl font-semibold text-gray-800">
                        Order: {order.order_number}
                    </h1>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(order.status)}`}>
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </span>
                    {order.shopify_id && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              Shopify Order
            </span>
                    )}
                </div>

                <div className="flex space-x-2">
                    {order.status === 'pending' && hasAnyRole(['admin', 'production']) && (
                        <button
                            onClick={processOrder}
                            disabled={processingOrder || (fulfillability && !fulfillability.fulfillable)}
                            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white
                ${(processingOrder || (fulfillability && !fulfillability.fulfillable))
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'}`}
                        >
                            {processingOrder ? (
                                <>
                                    <ClockIcon className="animate-spin h-5 w-5 mr-2" /> Processing...
                                </>
                            ) : (
                                 <>
                                     <CheckCircleIcon className="h-5 w-5 mr-2" /> Process Order
                                 </>
                             )}
                        </button>
                    )}

                    {hasRole('admin') && (
                        <Link
                            to={`/orders/${id}/edit`}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <PencilIcon className="h-5 w-5 mr-2" /> Edit Order
                        </Link>
                    )}
                </div>
            </div>

            {/* Order Details */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Order Details</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Order information and details.</p>
                </div>
                <div className="border-t border-gray-200">
                    <dl>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Order Number</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.order_number}</dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Customer</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {order.customer_name || 'Unknown Customer'}
                            </dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Order Date</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {moment(order.order_date).format('MMMM D, YYYY, h:mm A')}
                            </dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Status</dt>
                            <dd className="mt-1 sm:mt-0 sm:col-span-2">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(order.status)}`}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
                            </dd>
                        </div>
                        {order.shopify_id && (
                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Shopify ID</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.shopify_id}</dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>

            {/* Order Items */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Order Items</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Products included in this order.</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                SKU
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Product
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Variant
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Quantity
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {order.items && order.items.length > 0 ? (
                            order.items.map((item) => (
                                item?.product ? (
                                    <tr key={item.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <Link to={`/products/${item.product.id}`} className="text-primary-600 hover:underline">
                                                {item.product.sku}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.product.name || item.product.sku}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.variant.charAt(0).toUpperCase() + item.variant.slice(1)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.quantity}
                                        </td>
                                    </tr>
                                                ):(

                                    <tr key={item.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <Link to={`/products/${item.product?.id}`} className="text-primary-600 hover:underline">
                                                {item.name}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.product?.name || item.product?.sku}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.variant.charAt(0).toUpperCase() + item.variant.slice(1)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.quantity}
                                        </td>
                                    </tr>
                                )

                            ))
                        ) : (
                             <tr>
                                 <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                                     No items in this order.
                                 </td>
                             </tr>
                         )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Fulfillability Check */}
            {order.status === 'pending' && fulfillability && (
                <div className={`bg-white shadow overflow-hidden sm:rounded-lg border-l-4 ${
                    fulfillability.fulfillable ? 'border-green-500' : 'border-red-500'
                }`}>
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Fulfillability Check
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Checking if this order can be fulfilled based on current inventory.
                        </p>
                    </div>
                    <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                        <div className="flex items-center mb-4">
                            {fulfillability.fulfillable ? (
                                <>
                                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                                    <span className="text-green-700 font-medium">
                    This order can be fulfilled. All required parts are in stock.
                  </span>
                                </>
                            ) : (
                                 <>
                                     <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                                     <span className="text-red-700 font-medium">
                    This order cannot be fulfilled due to insufficient inventory.
                  </span>
                                 </>
                             )}
                        </div>

                        <h4 className="text-sm font-medium text-gray-900 mb-2">Required Parts</h4>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Part Type
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        SKU-Specific
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Product SKU
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Required
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Available
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {fulfillability.part_status.map((part, index) => (
                                    <tr key={index}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {part.part_type}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {part.sku_specific ? (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            Yes
                          </span>
                                            ) : (
                                                 <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            No
                          </span>
                                             )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {part.product_sku || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {part.required}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {part.available}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {part.sufficient ? (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Sufficient
                          </span>
                                            ) : (
                                                 <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Insufficient
                          </span>
                                             )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderDetails;
